import React, { useEffect, useState } from 'react';
import axios from '@/utils/axiosInstance';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import * as XLSX from 'xlsx';
import { FaFileExport } from "react-icons/fa";
import { ChevronsLeft, ChevronsRight } from 'lucide-react';

interface Stock {
  id: string;
  product_name: string;
  qty: number;
  qty_available: number;
  qty_reserved: number;
  procurement_lead_time?: number;
  avg_daily_out?: number;
}

const StockBalance = () => {
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const { logout } = useAuth();
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL;

  const getAuthHeader = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}` || '',
      'Content-Type': 'application/json',
    },
  });

  const fetchStocks = async () => {
    try {
      const res = await axios.get(`${API}/analytics/getStockBalance`, {
        ...getAuthHeader(),
        params: {
          page: currentPage,
          limit: rowsPerPage,
          search,
          sort: sortField,
          order: sortOrder,
        },
      });
      setStocks(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err: any) {
      if (err.response?.status === 401) {
        logout();
        navigate('/login');
      }
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleExport = () => {
    const exportData = stocks.map((item) => ({
      'Product': item.product_name || '-',
      'Qty': item.qty,
      'Available': item.qty_available,
      'Reserved': item.qty_reserved,
      'Lead Time (days)': item.procurement_lead_time ?? 0,
      'Avg Daily Out': Number(item.avg_daily_out || 0).toFixed(2),
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Stock');
    XLSX.writeFile(workbook, 'StockList.xlsx');
  };

  useEffect(() => {
    fetchStocks();
  }, [currentPage, rowsPerPage, sortField, sortOrder]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setCurrentPage(1);
      fetchStocks();
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  const maxPage = Math.ceil(total / rowsPerPage);

  return (
    <div className="p-1 mx-auto max-w-6xl">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Stock Balance</h2>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-2">
          <div className="relative w-full md:w-1/3">
            <input
              type="text"
              placeholder="Search..."
              className="border px-2 py-1 rounded text-[13px] w-full pr-8"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                &#10005;
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <select
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
              className="border rounded px-2 py-1 text-[13px]"
            >
              {[10, 25, 50, 100].map((num) => (
                <option key={num} value={num}>{num} / page</option>
              ))}
            </select>
            |
            <button
              onClick={handleExport}
              className="text-blue-600 hover:text-blue-800 text-lg"
              title="Export to Excel"
            >
              <FaFileExport />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto border rounded mb-2">
          <table className="min-w-full text-[13px]">
            <thead className="bg-gray-100">
              <tr>
                {[
                  { key: 'product', label: 'Product' },
                  { key: 'qty', label: 'Qty' },
                  { key: 'qty_available', label: 'Available' },
                  { key: 'qty_reserved', label: 'Reserved' },
                  { key: 'procurement_lead_time', label: 'Proc. LT (d)' },
                  { key: 'avg_daily_out', label: 'Avg Daily Out (90 d)' },
                ].map(({ key, label }) => {
                  const isSorted = sortField === key;
                  const arrow = isSorted ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : '';
                  return (
                    <th
                      key={key}
                      className={`border px-2 py-2 text-center cursor-pointer select-none ${isSorted ? 'bg-blue-50 text-blue-700 font-semibold' : ''}`}
                      onClick={() => handleSort(key)}
                    >
                      {label}{arrow}
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {stocks.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="border px-2 py-1">{item.product_name || '-'}</td>
                  <td className="border px-2 py-1 text-right">{new Intl.NumberFormat('en-US').format(item.qty || 0)}</td>
                  <td className="border px-2 py-1 text-right">{new Intl.NumberFormat('en-US').format(item.qty_available || 0)}</td>
                  <td className="border px-2 py-1 text-right">{new Intl.NumberFormat('en-US').format(item.qty_reserved || 0)}</td>
                  <td className="border px-2 py-1 text-right">{new Intl.NumberFormat('en-US').format(item.procurement_lead_time || 0)}</td>
                  <td className="border px-2 py-1 text-right">{new Intl.NumberFormat('en-US').format(item.avg_daily_out || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center mt-4 text-[13px] flex-wrap gap-2">
          <div>
            Showing {(currentPage - 1) * rowsPerPage + 1}–{Math.min(currentPage * rowsPerPage, total)} of {total}
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-2 py-1 border rounded">
              <ChevronsLeft size={18} />
            </button>
            <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} className="px-2 py-1 border rounded">«</button>
            {Array.from({ length: maxPage }, (_, i) => i + 1)
              .filter((page) => Math.abs(page - currentPage) <= 2)
              .map((page) => (
                <button
                  key={page}
                  className={`px-2 py-1 border rounded ${page === currentPage ? 'bg-blue-600 text-white' : 'hover:bg-gray-100'}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
            <button onClick={() => setCurrentPage(p => Math.min(p + 1, maxPage))} disabled={currentPage === maxPage} className="px-2 py-1 border rounded">»</button>
            <button onClick={() => setCurrentPage(maxPage)} disabled={currentPage === maxPage} className="px-2 py-1 border rounded">
              <ChevronsRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockBalance;
